import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Lightning, Plus } from '@phosphor-icons/react';

export default function QuickAdd({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: '', last_name: '', company: '', email: '', phone: '', status: 'lead',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dupes, setDupes] = useState([]);
  const [dupesLoading, setDupesLoading] = useState(false);
  const [bypassDupes, setBypassDupes] = useState(false);

  // Debounced duplicate check: 500ms after the user stops typing, hit the API
  useEffect(() => {
    setBypassDupes(false); // any field change resets the "save anyway" intent
    const email = form.email.trim();
    const fn = form.first_name.trim();
    const ln = form.last_name.trim();
    const co = form.company.trim();
    // We need at least one signal worth checking
    if (!email && !(fn && ln) && !(ln && co)) {
      setDupes([]);
      return;
    }
    const handle = setTimeout(async () => {
      setDupesLoading(true);
      try {
        const params = new URLSearchParams();
        if (email) params.set('email', email);
        if (fn) params.set('first_name', fn);
        if (ln) params.set('last_name', ln);
        if (co) params.set('company', co);
        const { data } = await api.get(`/contacts/duplicate-check?${params.toString()}`);
        setDupes(data.matches || []);
      } catch {
        setDupes([]);
      } finally {
        setDupesLoading(false);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [form.email, form.first_name, form.last_name, form.company]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setForm({ first_name: '', last_name: '', company: '', email: '', phone: '', status: 'lead' });
      setError('');
      setDupes([]);
      setBypassDupes(false);
    }
  }, [open]);

  const handleSave = async () => {
    if (!form.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (dupes.length > 0 && !bypassDupes) {
      setError('Possible duplicate found — review below or click "Save Anyway"');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post('/contacts', form);
      onOpenChange(false);
      navigate(`/contacts/${data.contact_id}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNew = async () => {
    if (!form.first_name.trim()) {
      setError('First name is required');
      return;
    }
    if (dupes.length > 0 && !bypassDupes) {
      setError('Possible duplicate found — review or click "Save Anyway"');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.post('/contacts', form);
      setForm({ first_name: '', last_name: '', company: '', email: '', phone: '', status: 'lead' });
      setDupes([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] rounded-sm border-[#E5E7EB] p-0 gap-0"
        data-testid="quick-add-dialog"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="px-5 pt-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base font-bold text-[#111827]">
            <Lightning size={18} weight="fill" className="text-[#002FA7]" />
            Quick Add Contact
          </DialogTitle>
          <p className="text-xs text-[#4B5563] mt-0.5">Press <kbd className="px-1.5 py-0.5 bg-[#F3F4F6] border border-[#E5E7EB] rounded text-[10px] font-mono">Cmd+Enter</kbd> to save</p>
        </DialogHeader>

        {error && (
          <div className="mx-5 mb-2 p-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-sm" data-testid="quick-add-error">
            {error}
          </div>
        )}

        {/* Duplicate warning */}
        {dupes.length > 0 && (
          <div className="mx-5 mb-2 p-3 bg-amber-50 border border-amber-300 rounded-sm" data-testid="quick-add-dupes">
            <div className="text-xs font-bold text-amber-900 mb-1">
              ⚠ Possible duplicate{dupes.length > 1 ? 's' : ''} already in your CRM:
            </div>
            <ul className="space-y-1 max-h-32 overflow-y-auto">
              {dupes.map(d => (
                <li key={d.contact_id} className="text-[11px] text-amber-900 flex justify-between items-center gap-2">
                  <span className="truncate">
                    <strong>{d.first_name} {d.last_name}</strong>
                    {d.company && <span> @ {d.company}</span>}
                    {d.email && <span className="text-amber-700 ml-1">— {d.email}</span>}
                  </span>
                  <a
                    href={`/contacts/${d.contact_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] text-blue-700 hover:underline shrink-0"
                    data-testid={`dupe-open-${d.contact_id}`}
                  >
                    Open →
                  </a>
                </li>
              ))}
            </ul>
            <label className="flex items-center gap-1.5 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={bypassDupes}
                onChange={(e) => setBypassDupes(e.target.checked)}
                className="w-3 h-3"
                data-testid="quick-add-bypass-dupes"
              />
              <span className="text-[11px] text-amber-900">Save anyway — these aren't the same person</span>
            </label>
          </div>
        )}

        <div className="px-5 pb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">First Name *</Label>
              <Input
                autoFocus
                value={form.first_name}
                onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
                placeholder="Jane"
                className="mt-1 rounded-sm h-9 text-sm"
                data-testid="quick-add-first-name"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Last Name</Label>
              <Input
                value={form.last_name}
                onChange={(e) => setForm(f => ({ ...f, last_name: e.target.value }))}
                placeholder="Doe"
                className="mt-1 rounded-sm h-9 text-sm"
                data-testid="quick-add-last-name"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#4B5563]">Company</Label>
            <Input
              value={form.company}
              onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))}
              placeholder="Acme Inc."
              className="mt-1 rounded-sm h-9 text-sm"
              data-testid="quick-add-company"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="jane@acme.com"
                className="mt-1 rounded-sm h-9 text-sm"
                data-testid="quick-add-email"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-[#4B5563]">Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+1 555 0123"
                className="mt-1 rounded-sm h-9 text-sm"
                data-testid="quick-add-phone"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#4B5563]">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger className="mt-1 rounded-sm h-9 text-sm" data-testid="quick-add-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="lead">Lead</SelectItem>
                <SelectItem value="customer">Customer</SelectItem>
                <SelectItem value="prospect">Prospect</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between px-5 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSaveAndNew}
            disabled={saving}
            className="text-xs text-[#4B5563] hover:text-[#111827] rounded-sm gap-1"
            data-testid="quick-add-save-and-new-btn"
          >
            <Plus size={14} />
            Save & Add Another
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#002FA7] hover:bg-[#002080] text-white rounded-sm text-sm h-8 px-4"
            data-testid="quick-add-save-btn"
          >
            {saving ? 'Saving...' : 'Save Contact'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
